import { memo, ChangeEvent, ClipboardEvent, KeyboardEvent, FocusEvent } from "react";
import { TextField, Autocomplete, Grid, Box } from "@mui/material";
import { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";

// Types and Interfaces
export interface DropdownOption {
  value: string | number;
  label: string;
}

export interface FormData {
  brand: string;
  mainCategory: string;
  category: string;
  subCategory: string;
  genericName: string;
  itemName: string;
  uom: string;
  packageType: string;
  unitsPerPackage: string;
  description: string;
  modelName: string;
}

export interface FormErrors {
  brand?: string;
  mainCategory?: string;
  category?: string;
  subCategory?: string;
  genericName?: string;
  itemName?: string;
  uom?: string;
  packageType?: string;
  unitsPerPackage?: string;
  modelName?: string;
  description?: string;
}

export interface ItemFormFieldsProps {
  formData: FormData;
  errors: FormErrors;
  brands: DropdownOption[];
  mainCategories: DropdownOption[];
  categories: DropdownOption[];
  subCategories: DropdownOption[];
  genericNames: DropdownOption[];
  uoms: DropdownOption[];
  packageTypes: DropdownOption[];
  handleInputChange: (field: string, value: string) => void;
  handleTextInputChange: (field: string, e: ChangeEvent<HTMLInputElement>) => void;
  handleTextBlur: (field: string, e: FocusEvent<HTMLInputElement>) => void;
  handleTextPaste: (field: string, e: ClipboardEvent<HTMLInputElement>) => void;
  handleDescriptionChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleUnitsPerPackageChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleKeyDownOnDropdown: (e: KeyboardEvent<HTMLDivElement>) => void;
  isEditMode: boolean;
}

const ItemFormFields = memo<ItemFormFieldsProps>(
  ({
    formData,
    errors,
    brands,
    mainCategories,
    categories,
    subCategories,
    genericNames,
    uoms,
    packageTypes,
    handleInputChange,
    handleTextInputChange,
    handleTextBlur,
    handleTextPaste,
    handleDescriptionChange,
    handleUnitsPerPackageChange,
    handleKeyDownOnDropdown,
    isEditMode,
  }) => {
    // Helper functions for Autocomplete
    const getOptionLabel = (option: DropdownOption): string => option.label || "";
    
    const findBrandValue = (): DropdownOption | null => {
      return brands.find((b) => String(b.value) === String(formData.brand)) || null;
    };
    
    const findMainCategoryValue = (): DropdownOption | null => {
      return mainCategories.find((mc) => String(mc.value) === String(formData.mainCategory)) || null;
    };
    
    const findCategoryValue = (): DropdownOption | null => {
      return categories.find((c) => String(c.value) === String(formData.category)) || null;
    };
    
    const findSubCategoryValue = (): DropdownOption | null => {
      return subCategories.find((sc) => String(sc.value) === String(formData.subCategory)) || null;
    };
    
    const findGenericNameValue = (): DropdownOption | null => {
      return genericNames.find((gn) => String(gn.value) === String(formData.genericName)) || null;
    };
    
    const findUomValue = (): DropdownOption | null => {
      return uoms.find((u) => String(u.value) === String(formData.uom)) || null;
    };
    
    const findPackageTypeValue = (): DropdownOption | null => {
      return packageTypes.find((pt) => String(pt.value) === String(formData.packageType)) || null;
    };

    const handleBrandChange = (event: any, value: DropdownOption | null): void => {
      handleInputChange("brand", value?.value?.toString() || "");
    };
    
    const handleMainCategoryChange = (event: any, value: DropdownOption | null): void => {
      handleInputChange("mainCategory", value?.value?.toString() || "");
    };
    
    const handleCategoryChange = (event: any, value: DropdownOption | null): void => {
      handleInputChange("category", value?.value?.toString() || "");
    };
    
    const handleSubCategoryChange = (event: any, value: DropdownOption | null): void => {
      handleInputChange("subCategory", value?.value?.toString() || "");
    };
    
    const handleGenericNameChange = (event: any, value: DropdownOption | null): void => {
      handleInputChange("genericName", value?.value?.toString() || "");
    };
    
    const handleUomChange = (event: any, value: DropdownOption | null): void => {
      handleInputChange("uom", value?.value?.toString() || "");
    };
    
    const handlePackageTypeChange = (event: any, value: DropdownOption | null): void => {
      handleInputChange("packageType", value?.value?.toString() || "");
    };

    const handleItemNameChange = (e: ChangeEvent<HTMLInputElement>): void => {
      handleTextInputChange("itemName", e);
    };
    
    const handleModelNameChange = (e: ChangeEvent<HTMLInputElement>): void => {
      handleTextInputChange("modelName", e);
    };
    
    const handleItemNameBlur = (e: FocusEvent<HTMLInputElement>): void => {
      handleTextBlur("itemName", e);
    };
    
    const handleModelNameBlur = (e: FocusEvent<HTMLInputElement>): void => {
      handleTextBlur("modelName", e);
    };
    
    const handleItemNamePaste = (e: ClipboardEvent<HTMLInputElement>): void => {
      handleTextPaste("itemName", e);
    };
    
    const handleModelNamePaste = (e: ClipboardEvent<HTMLInputElement>): void => {
      handleTextPaste("modelName", e);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
      if (formData.itemName === "" && e.key === " ") {
        e.preventDefault();
      }
    };

    const handleModelKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
      if (formData.modelName === "" && e.key === " ") {
        e.preventDefault();
      }
    };

    return (
      <>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* 1. Brand Name (mandatory) */}
              <Box>
                <Autocomplete
                  options={brands}
                  getOptionLabel={getOptionLabel}
                  value={findBrandValue()}
                  onChange={handleBrandChange}
                  disabled={isEditMode}
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                      {...params}
                      label="Brand Name"
                      required
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={!!errors.brand}
                      helperText={errors.brand}
                      FormHelperTextProps={{
                        sx: { fontSize: "12px", color: "error.main" },
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        sx: { "& .MuiFormLabel-asterisk": { color: "error.main" } },
                      }}
                      onKeyDown={handleKeyDownOnDropdown}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                      }}
                    />
                  )}
                  freeSolo={false}
                  autoSelect={false}
                />
              </Box>

              {/* 2. Item Main Category (mandatory) */}
              <Box>
                <Autocomplete
                  options={mainCategories}
                  getOptionLabel={getOptionLabel}
                  value={findMainCategoryValue()}
                  onChange={handleMainCategoryChange}
                  disabled={isEditMode}
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                      {...params}
                      label="Item Main Category"
                      required
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={!!errors.mainCategory}
                      helperText={errors.mainCategory}
                      FormHelperTextProps={{
                        sx: { fontSize: "12px", color: "error.main" },
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        sx: { "& .MuiFormLabel-asterisk": { color: "error.main" } },
                      }}
                      onKeyDown={handleKeyDownOnDropdown}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                      }}
                    />
                  )}
                  freeSolo={false}
                  autoSelect={false}
                />
              </Box>

              {/* 3. Item Category (mandatory) */}
              <Box>
                <Autocomplete
                  options={categories}
                  getOptionLabel={getOptionLabel}
                  value={findCategoryValue()}
                  onChange={handleCategoryChange}
                  disabled={isEditMode || !formData.mainCategory}
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                      {...params}
                      label="Item Category"
                      required
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={!!errors.category}
                      helperText={errors.category}
                      FormHelperTextProps={{
                        sx: { fontSize: "12px", color: "error.main" },
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        sx: { "& .MuiFormLabel-asterisk": { color: "error.main" } },
                      }}
                      placeholder={
                        formData.mainCategory
                          ? "Select category"
                          : "Select main category first"
                      }
                      onKeyDown={handleKeyDownOnDropdown}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                      }}
                    />
                  )}
                  freeSolo={false}
                  autoSelect={false}
                />
              </Box>

              {/* 4. Item Subcategory (mandatory) */}
              <Box>
                <Autocomplete
                  options={subCategories}
                  getOptionLabel={getOptionLabel}
                  value={findSubCategoryValue()}
                  onChange={handleSubCategoryChange}
                  disabled={isEditMode || !formData.category}
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                      {...params}
                      label="Item Subcategory"
                      required
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={!!errors.subCategory}
                      helperText={errors.subCategory}
                      FormHelperTextProps={{
                        sx: { fontSize: "12px", color: "error.main" },
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        sx: { "& .MuiFormLabel-asterisk": { color: "error.main" } },
                      }}
                      placeholder={
                        formData.category
                          ? "Select subcategory"
                          : "Select category first"
                      }
                      onKeyDown={handleKeyDownOnDropdown}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                      }}
                    />
                  )}
                  freeSolo={false}
                  autoSelect={false}
                />
              </Box>

              {/* 5. Generic Name (mandatory) */}
              <Box>
                <Autocomplete
                  options={genericNames}
                  getOptionLabel={getOptionLabel}
                  value={findGenericNameValue()}
                  onChange={handleGenericNameChange}
                  disabled={isEditMode || !formData.subCategory}
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                      {...params}
                      label="Generic Name"
                      required
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={!!errors.genericName}
                      helperText={errors.genericName}
                      FormHelperTextProps={{
                        sx: { fontSize: "12px", color: "error.main" },
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        sx: { "& .MuiFormLabel-asterisk": { color: "error.main" } },
                      }}
                      placeholder={
                        formData.subCategory
                          ? "Select generic name"
                          : "Select subcategory first"
                      }
                      onKeyDown={handleKeyDownOnDropdown}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                      }}
                    />
                  )}
                  freeSolo={false}
                  autoSelect={false}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* 6. Item Name (mandatory) */}
              <Box>
                <TextField
                  label="Item Name"
                  required
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={formData.itemName}
                  onChange={handleItemNameChange}
                  onBlur={handleItemNameBlur}
                  onPaste={handleItemNamePaste}
                  onKeyDown={handleKeyDown}
                  disabled={isEditMode}
                  error={!!errors.itemName}
                  helperText={errors.itemName}
                  FormHelperTextProps={{
                    sx: { fontSize: "12px", color: "error.main" },
                  }}
                  InputLabelProps={{
                    sx: { "& .MuiFormLabel-asterisk": { color: "error.main" } },
                  }}
                />
              </Box>

              {/* 7. Model name (optional) */}
              <Box>
                <TextField
                  label="Model Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={formData.modelName}
                  onChange={handleModelNameChange}
                  onBlur={handleModelNameBlur}
                  onPaste={handleModelNamePaste}
                  onKeyDown={handleModelKeyDown}
                  error={!!errors.modelName}
                  helperText={errors.modelName}
                  FormHelperTextProps={{
                    sx: { fontSize: "12px", color: "error.main" },
                  }}
                />
              </Box>

              {/* 8. Unit of Measure (UOM) - (mandatory) */}
              <Box>
                <Autocomplete
                  options={uoms}
                  getOptionLabel={getOptionLabel}
                  value={findUomValue()}
                  onChange={handleUomChange}
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                      {...params}
                      label="Unit of Measure (UOM)"
                      required
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={!!errors.uom}
                      helperText={errors.uom}
                      FormHelperTextProps={{
                        sx: { fontSize: "12px", color: "error.main" },
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        sx: { "& .MuiFormLabel-asterisk": { color: "error.main" } },
                      }}
                      onKeyDown={handleKeyDownOnDropdown}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                      }}
                    />
                  )}
                  freeSolo={false}
                  autoSelect={false}
                />
              </Box>

              {/* 9. Package Type (mandatory) */}
              <Box>
                <Autocomplete
                  options={packageTypes}
                  getOptionLabel={getOptionLabel}
                  value={findPackageTypeValue()}
                  onChange={handlePackageTypeChange}
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                      {...params}
                      label="Package Type"
                      required
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={!!errors.packageType}
                      helperText={errors.packageType}
                      FormHelperTextProps={{
                        sx: { fontSize: "12px", color: "error.main" },
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        sx: { "& .MuiFormLabel-asterisk": { color: "error.main" } },
                      }}
                      onKeyDown={handleKeyDownOnDropdown}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                      }}
                    />
                  )}
                  freeSolo={false}
                  autoSelect={false}
                />
              </Box>

              {/* 10. Units per Package (mandatory) - numbers only */}
              <Box>
                <TextField
                  label="Units per Package"
                  required
                  variant="outlined"
                  size="small"
                  fullWidth
                  type="text"
                  inputMode="numeric"
                  value={formData.unitsPerPackage}
                  onChange={handleUnitsPerPackageChange}
                  error={!!errors.unitsPerPackage}
                  helperText={errors.unitsPerPackage}
                  inputProps={{ maxLength: 9, pattern: "[0-9]*" }}
                  FormHelperTextProps={{
                    sx: { fontSize: "12px", color: "error.main" },
                  }}
                  InputLabelProps={{
                    sx: { "& .MuiFormLabel-asterisk": { color: "error.main" } },
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* 11. Description (Optional) */}
        <Box sx={{ mb: 4 }}>
          <TextField
            label="Description"
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={handleDescriptionChange}
          />
        </Box>
      </>
    );
  }
);

ItemFormFields.displayName = "ItemFormFields";

export default ItemFormFields;